import { SagaIterator } from '@redux-saga/core';
import { call, put, takeLeading, takeLatest } from 'redux-saga/effects';

import api from '~/store/api';
import { creators as notificationCreators } from '~/store/notification';

import { CarePathwayQueueCounts, CarePathwayQueueCountsByPathwayId } from '../api/types/nextPrescreening';
import { creators } from './actions';

function* getCarePathways(): SagaIterator {
  try {
    const carePathways = yield call(api.nextPrescreening.getCarePathways);
    const sortedCarePathways = [...carePathways].sort((a, b) => a.name.localeCompare(b.name));
    yield put(creators.setCareGapOptions({ carePathways: sortedCarePathways }));
  } catch (error) {
    yield put(creators.setCareGapOptions({ carePathways: [] }));
    yield put(notificationCreators.showErrorMessage(`Getting care pathways failed - ${error}`));
  }
}

export function* getCarePathwayQueueCounts(): SagaIterator {
  try {
    const counts: CarePathwayQueueCounts[] = yield call(api.nextPrescreening.getCarePathwayQueueCounts);
    const countsByPathwayId = counts.reduce<CarePathwayQueueCountsByPathwayId>(
      (acc, { carePathwayId, unscreened, rescreen }) => ({ ...acc, [carePathwayId]: { unscreened, rescreen } }),
      {},
    );
    yield put(creators.setCareGapOptions({ carePathwayQueueCounts: countsByPathwayId }));
  } catch {
    // Counts are decorative — a failure must never interrupt screening (AC3), so no error
    // toast. null keeps "unknown" distinct from "both queues empty".
    yield put(creators.setCareGapOptions({ carePathwayQueueCounts: null }));
  }
}

function* getCarePathwayFilterOptions({ carePathwayId }: { type: string; carePathwayId: string }): SagaIterator {
  try {
    if (carePathwayId) {
      const res = yield call(api.nextPrescreening.getCarePathwayFilters, carePathwayId);
      const { carePathwayStages: stages, carePathwaySites: sites } = res;
      const sortedSites = [...sites].sort((a, b) => a.name.localeCompare(b.name));
      yield put(creators.setCareGapOptions({ sites: sortedSites, stages }));
    } else {
      yield put(creators.setCareGapOptions({ stages: [], sites: [] }));
    }
  } catch (error) {
    yield put(creators.setCareGapOptions({ stages: [], sites: [] }));
    yield put(notificationCreators.showErrorMessage(`Getting care pathway filters failed - ${error}`));
  }
}

export default [
  takeLeading('GET_CARE_PATHWAYS', getCarePathways),
  takeLatest('GET_CARE_PATHWAY_FILTER_OPTIONS', getCarePathwayFilterOptions),
  takeLatest('GET_CARE_PATHWAY_QUEUE_COUNTS', getCarePathwayQueueCounts),
];
